/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Form, Grid, Header } from '@mairistem/composants';
import { NationaliteInput } from '@mairistem/administres-utils';

import type {
  Diplome,
  Individu,
  Profession,
  SituationScolaire,
  Specialite,
} from '@/types';
import { Qualification } from '@/enums';
import { QualificationInput } from '@/components/Common/Qualification';

import validationInformationsComplementaires from '@/utils/validations/validationInformationsComplementaires';
import PanelModalEdition from '../PanelModalEdition';

interface InformationsComplementairesModalProps {
  situationScolaire: SituationScolaire;
  diplome: Diplome;
  specialite: Specialite;
  profession: Profession;
  identifiantNationaliteBis: number;
  scolarise: boolean;
  activiteProfessionnelle: boolean;
  updated: boolean;
  onSave: () => Promise<void>;
  onChange: (data: Partial<Individu>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsComplementairesModal = (
  props: InformationsComplementairesModalProps,
) => {
  const {
    situationScolaire,
    diplome,
    specialite,
    profession,
    identifiantNationaliteBis,
    scolarise,
    activiteProfessionnelle,
    updated,
    onChange,
    onSave,
    onClose,
    onCancel,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Partial<Individu> & { api?: boolean }
  >({});
  const [hasDoubleNationalite, setHasDoubleNationalite] =
    React.useState<boolean>(() => !_.isNil(identifiantNationaliteBis));

  const handleChange = React.useCallback(
    (
      _e: React.FormEvent<HTMLInputElement>,
      data: { name: string; value: { identifiant: number } },
    ) => {
      onChange({
        [data.name]: data.value,
      });

      switch (data?.name) {
        case 'situationScolaire':
          onChange({ identifiantSituationScolaire: data?.value?.identifiant });
          break;
        case 'diplome':
          onChange({ identifiantDiplome: data?.value?.identifiant });
          break;
        case 'specialite':
          onChange({ identifiantSpecialite: data?.value?.identifiant });
          break;
        case 'profession':
          onChange({ identifiantProfession: data?.value?.identifiant });
          break;
      }
    },
    [onChange],
  );

  const handleNationaliteCheckboxChange = React.useCallback(() => {
    setHasDoubleNationalite((prev: boolean) => {
      if (prev) {
        onChange({
          identifiantNationaliteBis: null,
        });
      }
      return !prev;
    });
  }, [onChange]);

  const handleSituationChange = React.useCallback(
    (situation: string, value: boolean) => {
      onChange({
        [situation]: value,
      });
    },
    [onChange],
  );

  const handleSave = React.useCallback(() => {
    const e = validationInformationsComplementaires({
      hasDoubleNationalite,
      identifiantNationaliteBis,
      scolarise,
      activiteProfessionnelle,
    });

    setErrors(e);

    if (_.isEmpty(e)) {
      setLoading(true);

      onSave()
        .then(() => {
          onClose();
        })
        .catch(() => {
          setErrors({ api: true });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    hasDoubleNationalite,
    identifiantNationaliteBis,
    scolarise,
    activiteProfessionnelle,
    onClose,
    onSave,
  ]);

  return (
    <PanelModalEdition
      errors={errors}
      loading={loading}
      header="Informations complémentaires"
      updated={updated}
      onSave={handleSave}
      onClose={onCancel}
    >
      <Header as="h4">QUALIFICATIONS</Header>
      <Grid>
        <Grid.Row verticalAlign="middle">
          <Grid.Column
            width={4}
            style={{ display: 'flex', paddingLeft: '2em' }}
          >
            Scolarisé ou en formation ?
          </Grid.Column>
          <Grid.Column width={2} style={{ display: 'flex', alignItems: 'end' }}>
            <Form.Checkbox
              name="scolarise"
              label="Oui"
              checked={!!scolarise}
              onChange={() => handleSituationChange('scolarise', true)}
            />
          </Grid.Column>
          <Grid.Column width={2} style={{ display: 'flex', alignItems: 'end' }}>
            <Form.Checkbox
              name="scolarise"
              label="Non"
              checked={!scolarise && !_.isNil(scolarise)}
              onChange={() => handleSituationChange('scolarise', false)}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <QualificationInput
              qualification={Qualification.SituationScolaire}
              onChange={handleChange}
              value={situationScolaire}
              disabled={!scolarise}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8} style={{ paddingLeft: '2em' }}>
            <QualificationInput
              qualification={Qualification.Specialite}
              onChange={handleChange}
              value={specialite}
              disabled={!scolarise}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <QualificationInput
              qualification={Qualification.Diplome}
              onChange={handleChange}
              value={diplome}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row verticalAlign="middle">
          <Grid.Column
            width={4}
            style={{ display: 'flex', paddingLeft: '2em' }}
          >
            Activité professionnelle ?
          </Grid.Column>
          <Grid.Column width={2} style={{ display: 'flex', alignItems: 'end' }}>
            <Form.Checkbox
              name="activiteProfessionnelle"
              label="Oui"
              checked={!!activiteProfessionnelle}
              onChange={() =>
                handleSituationChange('activiteProfessionnelle', true)
              }
            />
          </Grid.Column>
          <Grid.Column width={2} style={{ display: 'flex', alignItems: 'end' }}>
            <Form.Checkbox
              name="activiteProfessionnelle"
              label="Non"
              checked={
                !activiteProfessionnelle && !_.isNil(activiteProfessionnelle)
              }
              onChange={() =>
                handleSituationChange('activiteProfessionnelle', false)
              }
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <QualificationInput
              qualification={Qualification.Profession}
              onChange={handleChange}
              value={profession}
              disabled={!activiteProfessionnelle}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Header as="h4">NATIONALITE</Header>
      <Grid>
        <Grid.Row>
          <Grid.Column
            width={8}
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '2em',
            }}
          >
            <Form.Checkbox
              name="doubleNationalité"
              data-testid="fiche-panel-infocomp-modal-edition-checkbox-exemption"
              label="Bénéficie d'une double nationalité - 2éme nationalité"
              checked={hasDoubleNationalite}
              onChange={handleNationaliteCheckboxChange}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <NationaliteInput
              bis
              identifiantNationalite={identifiantNationaliteBis}
              disabled={!hasDoubleNationalite}
              onChange={handleChange}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </PanelModalEdition>
  );
};
export default InformationsComplementairesModal;
